import React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Pencil } from 'lucide-react';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import { OFFER_UNITS } from '../../../utils/offerUnits';

const PANEL_MIN_WIDTH = 208;
const PANEL_MAX_HEIGHT = 288;
const PANEL_GAP = 4;
const VIEWPORT_MARGIN = 8;

const STANDARD_UNIT_VALUES = new Set(OFFER_UNITS.map((u) => u.value));

type PanelPosition = { top?: number; bottom?: number; left: number; width: number };

export type OfferUnitPickerProps = {
  value: string;
  onChange: (unit: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
  triggerClassName?: string;
  /** `page` erzwingt helle Panel-Farben im A4-Dokument, `app` folgt dem App-Theme. */
  variant?: 'page' | 'app';
};

/**
 * Einheiten-Auswahl im App-Design statt nativer datalist.
 * Freie Einheiten aus Altdokumenten bleiben erhalten und lassen sich manuell setzen.
 */
export function OfferUnitPicker({
  value,
  onChange,
  disabled = false,
  ariaLabel = 'Einheit',
  triggerClassName = '',
  variant = 'app',
}: OfferUnitPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [manualMode, setManualMode] = React.useState(false);
  const [manualValue, setManualValue] = React.useState('');
  const [pos, setPos] = React.useState<PanelPosition | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const manualInputRef = React.useRef<HTMLInputElement>(null);

  const current = value.trim();
  const isCustom = current.length > 0 && !STANDARD_UNIT_VALUES.has(current);

  const updatePos = React.useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.max(PANEL_MIN_WIDTH, rect.width);
    let left = rect.left;
    if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - width);
    }
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow;
    setPos({
      top: openUp ? undefined : rect.bottom + PANEL_GAP,
      bottom: openUp ? window.innerHeight - rect.top + PANEL_GAP : undefined,
      left,
      width,
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePos();
    const onScroll = (e: Event) => {
      // Scrollen in der Liste selbst darf das Panel nicht schließen.
      const target = e.target as Node | null;
      if (target && panelRef.current?.contains(target)) return;
      const rect = btnRef.current?.getBoundingClientRect();
      // Panel folgt dem Trigger; erst wenn der aus dem Viewport läuft, schließen.
      if (!rect || rect.bottom < 0 || rect.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      updatePos();
    };
    const onResize = () => updatePos();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePos]);

  React.useEffect(() => {
    if (!open) {
      setManualMode(false);
      return;
    }
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  React.useEffect(() => {
    if (manualMode) manualInputRef.current?.focus();
  }, [manualMode]);

  const select = (next: string) => {
    setOpen(false);
    if (next !== value) onChange(next);
  };

  const commitManual = () => {
    select(manualValue.trim());
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`flex w-full min-w-0 items-center justify-between gap-1 rounded-md border border-app-border bg-app-surface px-2 text-left text-app-text hover:bg-app-hover disabled:opacity-60 ${triggerClassName}`}
      >
        <span className={`truncate ${current ? '' : 'text-app-text-subtle'}`}>{current || '-'}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-app-text-subtle" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              style={{
                position: 'fixed',
                top: pos.top,
                bottom: pos.bottom,
                left: pos.left,
                width: pos.width,
                maxHeight: PANEL_MAX_HEIGHT,
                zIndex: UI_Z_INDEX.portalDropdown,
              }}
              className="flex flex-col overflow-hidden rounded-md border border-app-border bg-app-surface text-sm shadow-lg"
            >
              {manualMode ? (
                <div className="flex flex-col gap-2 p-2">
                  <input
                    ref={manualInputRef}
                    value={manualValue}
                    maxLength={24}
                    onChange={(e) => setManualValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitManual();
                      }
                    }}
                    placeholder="z. B. Rolle"
                    className="h-9 w-full rounded-md border border-app-border bg-app-surface px-2 text-sm text-app-text outline-none focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20"
                    aria-label="Eigene Einheit"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={commitManual}
                      className="inline-flex h-9 flex-1 items-center justify-center rounded-md bg-pipedrive-blue px-3 text-sm font-semibold text-white hover:bg-blue-700"
                    >
                      Übernehmen
                    </button>
                    <button
                      type="button"
                      onClick={() => setManualMode(false)}
                      className="inline-flex h-9 items-center justify-center rounded-md border border-app-border px-3 text-sm text-app-text-muted hover:bg-app-hover"
                    >
                      Zurück
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div role="listbox" aria-label={ariaLabel} className="app-scrollbar min-h-0 flex-1 overflow-y-auto py-1">
                    <UnitOption label="Ohne Einheit" selected={current === ''} onSelect={() => select('')} muted />
                    {isCustom ? (
                      <UnitOption label={current} hint="eigene Einheit" selected onSelect={() => select(current)} />
                    ) : null}
                    {OFFER_UNITS.map((unit) => (
                      <UnitOption
                        key={unit.value}
                        label={unit.label}
                        selected={unit.value === current}
                        onSelect={() => select(unit.value)}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setManualValue(current);
                      setManualMode(true);
                    }}
                    className="flex shrink-0 items-center gap-2 border-t border-app-border px-3 py-2 text-left text-app-text-muted hover:bg-app-hover"
                  >
                    <Pencil className="h-3.5 w-3.5 shrink-0" />
                    Eigene Einheit …
                  </button>
                </>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function UnitOption({
  label,
  hint,
  selected,
  muted = false,
  onSelect,
}: {
  label: string;
  hint?: string;
  selected: boolean;
  muted?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
        selected ? 'bg-app-surface-muted font-medium text-app-text' : 'hover:bg-app-hover'
      } ${muted && !selected ? 'text-app-text-subtle' : 'text-app-text'}`}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint ? <span className="shrink-0 text-xs text-app-text-subtle">{hint}</span> : null}
      {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-pipedrive-blue" /> : null}
    </button>
  );
}
